/**
 * Temporary screen-scoped design tokens for **Settings → Team**.
 *
 * Mirrors the Settings design (`Settings.dc.html`, "Teams" section) 1:1 — every
 * colour, size, radius, shadow, spacing and type step the seats card and the
 * member table need is captured here so the components never carry a raw
 * literal. These stay screen-local on purpose: once every settings screen has
 * been re-skinned the shared values get promoted into the MUI theme and this
 * file is deleted.
 */

import {
  button,
  contentCard,
  formField,
  inkfree,
  notification,
  signer,
  switchControl,
  white,
} from "theme/colors";

export const teamTokens = {
  color: {
    /** Deep brand ink — member names and cell text. */
    ink: signer.ink,
    /** Secondary cell text (phone, email) and the seat usage line. */
    muted: signer.text.meta,
    /** Column-header strip text + the right-aligned seat note. */
    columnHeader: signer.text.faint,
    surface: white,
    /** Card chrome shared by the seats card and the table card. */
    cardBorder: contentCard.borderMuted,
    /** Column-header strip fill + its bottom rule / footer top rule. */
    headerBg: signer.surface.subtle,
    headerBorder: signer.border.light,
    /** Rule between member rows. */
    rowDivider: signer.border.divider,
    /** Monogram avatar in the NAME cell. */
    avatarBg: signer.surface.muted,
    avatarText: signer.ink,
    /** Role pill. */
    rolePillText: signer.orange.dark,
    rolePillBg: inkfree.light,
    /** STATUS column. */
    statusActive: notification.success.icon,
    statusLocked: notification.error.icon,
    statusInactive: signer.text.faint,
    /** Invited but not yet accepted — already billable, so it isn't grey. */
    statusInvited: signer.orange.dark,
    /** Link text/hover + the seat-meter's own fill — a thin accent, stays orange. */
    accent: signer.orange.main,
    accentHover: signer.orange.dark,
    /**
     * "Send Invite" button + active page item — solid fills, so per the
     * standing brand-colour rule these are the app brand red, not `accent`
     * above.
     */
    ctaBg: inkfree.main,
    ctaBgHover: inkfree.dark,
    onAccent: white,
    /** "Send Invite" once every seat is taken. */
    disabledBg: button.disabledBg,
    disabledText: signer.text.faint,
    disabledBorder: signer.border.light,
    /** Seat meter. */
    seatTrack: signer.border.divider,
    /** 80%+ of the seat allowance consumed. */
    seatFillWarning: signer.orange.main,
    seatFillFull: notification.error.icon,
    /** Outlined "Manage seats" action. */
    manageBorder: formField.border,
    manageBorderHover: signer.ink,
    /** Row overflow (⋮) glyph. */
    actionGlyph: signer.text.faint,
    /** Table's own horizontal scrollbar thumb. */
    scrollbarThumb: "#D6D2E4",
    /** Inactive pagination item. */
    pageItem: signer.text.meta,
    pageHoverFill: signer.surface.muted,
    /** Disabled pagination arrow. */
    pageDisabled: switchControl.trackOff,
    emptyText: signer.text.meta,
  },

  font: {
    inviteButton: "13px",
    seatsLabel: "12.5px",
    seatsUsage: "12px",
    seatsNote: "11.5px",
    manageButton: "12.5px",
    columnHeader: "10.5px",
    cell: "13px",
    avatar: "10px",
    rolePill: "11.5px",
    status: "12px",
    pageItem: "12px",
    empty: "12.5px",
  },

  weight: {
    strong: 700,
    medium: 600,
    regular: 400,
  },

  letterSpacing: {
    columnHeader: "0.09em",
    action: "1px",
  },

  radius: {
    card: "14px",
    control: "9px",
    pageItem: "7px",
    pill: "999px",
    avatar: "50%",
  },

  border: {
    hairline: "1px",
  },

  shadow: {
    card: "0 1px 3px rgba(33, 21, 71, 0.04)",
  },

  space: {
    /** Horizontal padding shared by the header strip, every row and the footer. */
    gutter: "28px",
    seatsPaddingY: "16px",
    seatsPaddingX: "24px",
    /** Gap between the seat meter block and the "Manage seats" action. */
    seatsGap: "20px",
    /** Gap between the seat usage line and the meter. */
    seatsStackGap: "7px",
    /** Gap between "Seats" and the usage sentence. */
    seatsInlineGap: "10px",
    columnHeaderY: "10px",
    rowY: "13px",
    footerY: "14px",
    /** Gap between the avatar and the member name. */
    nameGap: "10px",
    /** Keeps a long email clear of the ROLE column. */
    emailPaddingRight: "12px",
    paginationGap: "4px",
    inviteIconGap: "8px",
    emptyY: "34px",
  },

  size: {
    inviteButtonHeight: "40px",
    inviteButtonPaddingX: "20px",
    inviteIcon: 14,
    inviteIconStrokeWidth: 2,
    manageButtonHeight: "36px",
    manageButtonPaddingX: "16px",
    seatTrackHeight: "6px",
    avatar: "28px",
    pageItem: "28px",
    pageArrow: "11px",
    pageArrowStrokeWidth: 2.5,
    /** Below this the six columns stop fitting, so the table scrolls instead. */
    tableMinWidth: 820,
    /** Thickness of the table's own horizontal scrollbar — the browser
     *  default reads heavy against a compact row strip. */
    tableScrollbarHeight: "6px",
    tableScrollbarRadius: "8px",
  },

  /** Grid template copied verbatim from the design. */
  grid: {
    row: "1.2fr 1fr 1.6fr 130px 90px 40px",
  },
} as const;

/**
 * Phone and tablet values for **Settings → Teams**, traced from
 * `Settings - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375, 390 and 834 identically — only the gutters around
 * the cards change, and `SettingsLayout` already owns those. Applied below
 * `md`; the desktop table is untouched.
 */
export const teamResponsiveTokens = {
  /** Full-width actions above the list. */
  action: { height: "44px", paddingX: "20px", fontSize: "13px" },
  seats: {
    padding: "15px 16px",
    gap: "10px",
    inlineGap: "9px",
    labelFontSize: "12.5px",
    usageFontSize: "12px",
    noteFontSize: "11px",
    buttonHeight: "44px",
    buttonPaddingX: "16px",
    buttonFontSize: "12.5px",
  },
  /** One member, drawn as their own card instead of a table row. */
  card: {
    border: contentCard.borderMuted,
    radius: "14px",
    padding: "14px 16px",
    gap: "12px",
    textGap: "5px",
    avatar: "38px",
    avatarFontSize: "12px",
    nameGap: "8px",
    nameFontSize: "13.5px",
    statusFontSize: "11px",
    emailFontSize: "11.5px",
    phoneFontSize: "11.5px",
    pillFontSize: "11px",
    pillPadding: "3px 10px",
  },
  pager: {
    itemSize: "34px",
    itemRadius: "9px",
    fontSize: "12.5px",
    gap: "6px",
    paddingY: "4px",
    idleBorder: formField.border,
  },
} as const;

export default teamTokens;
